export class LoopEngine {
  constructor({
    blocks = [],
    currentIndex = 0,
    status = "idle",
    targetEndTime = null,
    remainingMs = null,
    segmentDurationMs = null
  } = {}) {
    this.blocks = blocks
    this.currentIndex = currentIndex
    this.status = status
    this.targetEndTime = targetEndTime
    this.remainingMs = remainingMs
    this.segmentDurationMs = segmentDurationMs
  }

  startCountdown(duration, now = Date.now(), { segmentDurationMs = duration } = {}) {
    const segmentMs = Number(duration)
    const segmentDuration = Number(segmentDurationMs)
    const startMs = Number(now)

    this.segmentDurationMs = segmentDuration
    this.targetEndTime = startMs + segmentMs
    this.remainingMs = null
    this.status = "running"
  }

  currentBlock() {
    return this.blocks[this.currentIndex]
  }

  nextIndex() {
    if (this.blocks.length === 0) return 0
    return (this.currentIndex + 1) % this.blocks.length
  }

  blockDurationMs(index = this.currentIndex) {
    const block = this.blocks[index]
    return block ? Number(block.durationMs) || 0 : 0
  }

  remainingMsAt(now = Date.now()) {
    const at = Number(now)

    if (this.status === "awaiting_switch") {
      return 0
    }

    if (this.status === "running" && this.targetEndTime != null) {
      return Math.max(0, Number(this.targetEndTime) - at)
    }

    if (this.status === "paused") {
      if (this.remainingMs != null) {
        return Math.max(0, Number(this.remainingMs))
      }

      return 0
    }

    if (this.remainingMs != null) {
      return Number(this.remainingMs) || 0
    }

    return this.blockDurationMs()
  }

  play(now = Date.now()) {
    if (this.blocks.length === 0) {
      return { ok: false, error: "no_blocks" }
    }

    if (this.status === "running") {
      return { ok: true }
    }

    if (this.status === "awaiting_switch") {
      return { ok: false, error: "awaiting_switch" }
    }

    const duration = this.remainingMsAt(now)
    const pausedSegmentDuration = Number(this.segmentDurationMs)
    const segmentDurationMs =
      this.status === "paused" && Number.isFinite(pausedSegmentDuration) && pausedSegmentDuration > 0
        ? pausedSegmentDuration
        : duration

    this.startCountdown(duration, now, { segmentDurationMs })

    return { ok: true }
  }

  pause(now = Date.now()) {
    if (this.status !== "running") {
      return { ok: true }
    }

    const remaining = Math.max(0, Number(this.targetEndTime) - Number(now))
    const blockDuration = this.blockDurationMs()

    // Store the full block length so refresh shows correct ring progress, not 100%.
    if (blockDuration > 0) {
      this.segmentDurationMs = blockDuration
    }

    this.remainingMs = remaining
    this.targetEndTime = null
    this.status = "paused"

    return { ok: true }
  }

  resume(now = Date.now()) {
    return this.play(now)
  }

  skip(now = Date.now()) {
    if (this.blocks.length === 0) {
      return { ok: false, error: "no_blocks" }
    }

    const to = this.nextIndex()
    return this.jumpTo(to, now, { fromSkip: true })
  }

  jumpTo(index, now = Date.now(), { fromSkip = false, start = false } = {}) {
    if (this.blocks.length === 0) {
      return { ok: false, error: "no_blocks" }
    }

    const targetIndex =
      ((index % this.blocks.length) + this.blocks.length) % this.blocks.length
    const from = this.currentIndex
    const changed = from !== targetIndex
    const wasRunning = this.status === "running"
    const duration = this.blockDurationMs(targetIndex)

    this.currentIndex = targetIndex

    if (this.status === "running" || this.status === "awaiting_switch" || fromSkip || start) {
      this.startCountdown(duration, now)
    } else if (this.status === "paused") {
      this.remainingMs = duration
      this.segmentDurationMs = duration
      this.targetEndTime = null
    } else {
      this.remainingMs = duration
      this.segmentDurationMs = duration
      this.targetEndTime = null
    }

    return {
      ok: true,
      from,
      to: targetIndex,
      changed,
      dispatchTransition: wasRunning && changed,
      fromLabel: this.blocks[from]?.label,
      toLabel: this.blocks[targetIndex]?.label,
      toDurationMs: duration
    }
  }

  acknowledgeSwitch(now = Date.now()) {
    if (this.status !== "awaiting_switch") {
      return { ok: false, error: "not_awaiting_switch" }
    }

    const from = this.currentIndex
    this.currentIndex = this.nextIndex()
    const to = this.currentIndex
    const duration = this.blockDurationMs(to)
    this.startCountdown(duration, now)

    return { ok: true, from, to }
  }

  reset() {
    this.currentIndex = 0
    this.status = "idle"
    this.targetEndTime = null
    this.remainingMs = null
    this.segmentDurationMs = null

    return { ok: true }
  }

  resetCurrentBlock(now = Date.now()) {
    if (this.blocks.length === 0) {
      return { ok: false, error: "no_blocks" }
    }

    const duration = this.blockDurationMs()

    if (this.status === "running") {
      this.startCountdown(duration, now)
    } else if (this.status === "awaiting_switch") {
      this.status = "paused"
      this.targetEndTime = null
      this.remainingMs = duration
      this.segmentDurationMs = duration
    } else {
      this.remainingMs = duration
      this.segmentDurationMs = duration
      this.targetEndTime = null
    }

    return { ok: true }
  }

  tick(now = Date.now()) {
    if (this.status !== "running" || this.targetEndTime == null) {
      return { transitioned: false }
    }

    if (now < this.targetEndTime) {
      return { transitioned: false }
    }

    const from = this.currentIndex
    const to = this.nextIndex()
    this.targetEndTime = null
    this.remainingMs = 0
    this.status = "awaiting_switch"

    return {
      transitioned: true,
      awaitingSwitch: true,
      from,
      to,
      fromLabel: this.blocks[from]?.label,
      toLabel: this.blocks[to]?.label,
      toDurationMs: this.blockDurationMs(to)
    }
  }

  catchUp(now = Date.now()) {
    if (this.status !== "running" || this.targetEndTime == null) {
      return {
        caughtUp: false,
        transitions: [],
        lastTransition: null
      }
    }

    if (now < this.targetEndTime) {
      return {
        caughtUp: false,
        transitions: [],
        lastTransition: null
      }
    }

    const result = this.tick(now)

    return {
      caughtUp: result.transitioned,
      transitions: result.transitioned ? [result] : [],
      lastTransition: result.transitioned ? result : null
    }
  }

  snapshot() {
    if (this.status === "running") {
      return {
        current_index: this.currentIndex,
        status: this.status,
        target_end_time: this.targetEndTime,
        remaining_ms: null,
        segment_duration_ms: this.segmentDurationMs
      }
    }

    if (this.status === "awaiting_switch") {
      return {
        current_index: this.currentIndex,
        status: this.status,
        target_end_time: null,
        remaining_ms: 0,
        segment_duration_ms: null
      }
    }

    return {
      current_index: this.currentIndex,
      status: this.status,
      target_end_time: null,
      remaining_ms: this.remainingMs,
      segment_duration_ms: this.segmentDurationMs
    }
  }
}
